import { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Popconfirm, message, Select } from "antd";
import dayjs from "dayjs";
import AuthContext from "../../context/AuthContext";
import { useTicket, useUpdateTicket, usePromoteTicket, useDeleteTicket } from "../../queries/tickets";
import { useStaffDirectory, StaffMember } from "../../queries/users";
import { useSlaTargets } from "../../queries/settings";
import { ApiError, apiDownload } from "../../hooks/apiRequest";
import { firstLine } from "../../utils/text";
import { formatBytes } from "../../utils/attachments";
import { triggerBrowserDownload } from "../../utils/download";
import { PaperClipOutlined } from "@ant-design/icons";
import type { TicketAttachment } from "../../types/interfaces";
import { SEVERITY_OPTIONS, PRIORITY_OPTIONS } from "../../utils/levels";
import { formatMinutes } from "../../utils/duration";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { PageShell } from "../../components/ui/PageShell";
import { StatusTag, PriorityTag, SeverityTag } from "../../components/ui/StatusTag";
import { CommentThread } from "./CommentThread";

export const TicketDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userData, isManager } = useContext(AuthContext);
  const { data: ticket, isLoading, isError, error } = useTicket(id!);
  const updateTicket = useUpdateTicket();
  const promoteTicket = usePromoteTicket(id!);
  const deleteTicket = useDeleteTicket();
  const isStaff = userData?.role !== "requester";
  const { data: staff } = useStaffDirectory({ enabled: isStaff });
  const { data: sla } = useSlaTargets();
  const saveError = (err: unknown) => message.error(err instanceof ApiError ? err.message : "Could not update the ticket");

  // Response-time clocks: first response (created -> first staff reply) and
  // resolution (created -> solved), against the SLA target for the severity.
  const clock = (from: string, to: string | null, limitMin?: number) => {
    const minutes = Math.max(0, dayjs(to ?? undefined).diff(dayjs(from), "minute"));
    const breached = limitMin != null && minutes > limitMin;
    return (
      <span>
        <span className="osi-num" style={{ color: breached ? "var(--osi-danger)" : undefined }}>{formatMinutes(minutes)}</span>
        {limitMin != null && (
          <span style={{ color: "var(--osi-text-faint)" }}> of {formatMinutes(limitMin)} · {breached ? "SLA missed" : to ? "SLA met" : "within SLA"}</span>
        )}
      </span>
    );
  };

  const canPromote = isStaff && !ticket?.promotedTaskId;

  // Every role that can reach this page can PUT (manager on any ticket,
  // agent within their projects, requester on tickets they reported) —
  // updateTicket lets a requester change `solved` only, so this button only
  // ever needs to send { solved }.
  const handleToggleSolved = async () => {
    try {
      await updateTicket.mutateAsync({ id: id!, data: { solved: !ticket!.solved } });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not update the ticket");
    }
  };

  const handlePromote = async () => {
    try {
      await promoteTicket.mutateAsync();
      message.success("Ticket promoted to a task");
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not promote the ticket");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTicket.mutateAsync(id!);
      navigate("/support");
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not delete the ticket");
    }
  };

  return (
    <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
      {ticket && (
        <PageShell
          kind="detail"
          crumbs={[{ label: "Support", to: "/support" }, { label: "Ticket" }]}
          title={firstLine(ticket.subject, 120)}
          meta={[
            {
              label: "Status",
              value: ticket.solved
                ? <StatusTag label="Solved" tone="ok" dot />
                : <StatusTag label="Open" tone="warn" dot />,
            },
            {
              label: "Severity",
              value: !isStaff
                ? <SeverityTag severity={ticket.severity} />
                : (
                  <Select
                    size="small"
                    value={ticket.severity}
                    style={{ width: 120 }}
                    options={SEVERITY_OPTIONS}
                    onChange={(severity) => updateTicket.mutate({ id: id!, data: { severity } }, { onError: saveError })}
                  />
                ),
            },
            {
              label: "Priority",
              value: !isStaff
                ? <PriorityTag priority={ticket.priority} />
                : (
                  <Select
                    size="small"
                    value={ticket.priority}
                    style={{ width: 120 }}
                    options={PRIORITY_OPTIONS}
                    onChange={(priority) => updateTicket.mutate({ id: id!, data: { priority } }, { onError: saveError })}
                  />
                ),
            },
            ...(isStaff
              ? [{
                  label: "Assignee",
                  value: (
                    <Select
                      size="small"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      value={ticket.assigneeId ?? undefined}
                      placeholder="Unassigned"
                      style={{ width: 160 }}
                      options={(staff || []).map((u: StaffMember) => ({ label: u.name, value: u._id }))}
                      onChange={(assigneeId) => updateTicket.mutate({ id: id!, data: { assigneeId: assigneeId || null } }, { onError: saveError })}
                    />
                  ),
                }]
              : []),
            {
              label: "First response",
              value: ticket.firstResponseAt
                ? clock(ticket.createdAt, ticket.firstResponseAt, sla?.[ticket.severity]?.firstResponseMinutes)
                : <span style={{ color: "var(--osi-warn)" }}>
                    Waiting {clock(ticket.createdAt, null, sla?.[ticket.severity]?.firstResponseMinutes)}
                  </span>,
            },
            {
              label: "Resolution",
              value: ticket.resolvedAt
                ? clock(ticket.createdAt, ticket.resolvedAt, sla?.[ticket.severity]?.resolutionMinutes)
                : <span style={{ color: "var(--osi-text-faint)" }}>Open</span>,
            },
            { label: "Raised", value: <span className="osi-num">{dayjs(ticket.createdAt).format("D MMM YYYY")}</span> },
            { label: "Replies", value: <span className="osi-num">{ticket.comments.length}</span> },
            ...(ticket.promotedTaskId
              ? [{ label: "Linked task", value: (
                  <Button type="link" style={{ padding: 0, height: "auto", fontWeight: 600 }}
                    onClick={() => navigate(`/tasks/${ticket.promotedTaskId}`)}>
                    View task →
                  </Button>
                ) }]
              : []),
          ]}
          actions={
            <>
              <Button onClick={handleToggleSolved} loading={updateTicket.isPending}>
                {ticket.solved ? "Reopen" : "Mark solved"}
              </Button>
              {canPromote && (
                <Button onClick={handlePromote} loading={promoteTicket.isPending}>Promote to task</Button>
              )}
              {isManager && (
                <Popconfirm title="Delete this ticket?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={handleDelete}>
                  <Button danger>Delete</Button>
                </Popconfirm>
              )}
            </>
          }
        >
          {(ticket.subject.trim() !== firstLine(ticket.subject, 120) || (ticket.attachments || []).length > 0) && (
            <section
              aria-label="Original request"
              style={{
                marginBottom: 16,
                padding: 20,
                background: "var(--osi-surface)",
                border: "1px solid var(--osi-border)",
                borderRadius: "var(--osi-radius)",
              }}
            >
              <div className="osi-kicker" style={{ marginBottom: 8 }}>Request</div>
              <p style={{ margin: 0, whiteSpace: "pre-wrap", maxWidth: "var(--osi-reading-width)" }}>{ticket.subject}</p>
              {(ticket.attachments || []).length > 0 && (
                <ul style={{ listStyle: "none", margin: "16px 0 0", padding: 0, display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {ticket.attachments.map((file: TicketAttachment) => (
                    <li key={file._id}>
                      <Button
                        size="small"
                        icon={<PaperClipOutlined />}
                        onClick={async () => {
                          try {
                            const { blob } = await apiDownload(`files/${file._id}`);
                            triggerBrowserDownload(blob, file.filename);
                          } catch (err) {
                            message.error(err instanceof ApiError ? err.message : "Could not download the file");
                          }
                        }}
                      >
                        {file.filename} · {formatBytes(file.size)}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
          <CommentThread ticketId={ticket._id} comments={ticket.comments} />
        </PageShell>
      )}
    </DisplayComponent>
  );
};
